// Derived from https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document

const BUTTON_LABEL_CHOOSE = 'Choose Date';
const BUTTON_LABEL_CHANGE = 'Change Date';

const DAY_LABELS = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
];

const MONTH_LABELS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

function zeroPad( value ) {
	return value.toString().padStart( 2, '0' );
}

function formatDate( date ) {
	return date.getFullYear() + '-' + zeroPad( date.getMonth() + 1 ) + '-' + zeroPad( date.getDate() );
}

function isSameDay( date1, date2 ) {
	return (
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth() &&
		date1.getDate() === date2.getDate()
	);
}

function isSameMonth( date1, date2 ) {
	return (
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth()
	);
}

function isDisabled( node ) {
	return node.classList.contains( 'disabled' );
}

function getDateFromDateData( node ) {
	const parts = node.dataset.date.split( '-' );
	return new Date( parts[ 0 ], parseInt( parts[ 1 ] ) - 1, parts[ 2 ] );
}

class DatePickerDialog {
	constructor( container, searchEndpoint ) {
		this.searchEndpoint = searchEndpoint;

		this.messageCursorKeys = 'Cursor keys can navigate dates';
		this.lastMessage = '';

		this.inputNode = container.querySelector( 'input[type="date"]' );
		this.toggleButtonNode = container.querySelector( '.js-datepicker-toggle' );

		this.dialogNode = container.querySelector( '[role="dialog"]' );

		this.noticeNode = this.dialogNode.querySelector( '.js-datepicker-notice' );
		this.messageNode = this.dialogNode.querySelector( '.js-datepicker-helptext' );

		this.titleNode = this.dialogNode.querySelector( '.js-datepicker-title' );

		this.prevMonthButtonNode = this.dialogNode.querySelector( '.js-datepicker-prev' );
		this.nextMonthButtonNode = this.dialogNode.querySelector( '.js-datepicker-next' );

		this.calendarBodyNode = this.dialogNode.querySelector( 'table.js-datepicker-calendar tbody' );

		this.lastRowNode = null;

		this.days = [];

		this.focusedDay = new Date();
		this.selectedDay = new Date( 0, 0, 1 );

		this.isMouseDownOnBackground = false;

		this.init();
	}

	init() {
		this.inputNode.addEventListener(
			'blur',
			this.setDateForButtonLabel.bind( this ),
		);

		this.toggleButtonNode.addEventListener(
			'keydown',
			this.handleButtonKeydown.bind( this ),
		);
		this.toggleButtonNode.addEventListener( 'click', this.handleButtonClick.bind( this ) );

		this.prevMonthButtonNode.addEventListener(
			'click',
			this.handlePreviousMonthButton.bind( this ),
		);
		this.prevMonthButtonNode.addEventListener(
			'keydown',
			this.handlePreviousMonthButton.bind( this ),
		);

		this.nextMonthButtonNode.addEventListener(
			'click',
			this.handleNextMonthButtonNode.bind( this ),
		);
		this.nextMonthButtonNode.addEventListener(
			'keydown',
			this.handleNextMonthButtonNode.bind( this ),
		);

		document.body.addEventListener(
			'mouseup',
			this.handleBackgroundMouseUp.bind( this ),
			true,
		);

		// Create Grid of Dates
		this.calendarBodyNode.innerHTML = '';
		for ( let i = 0; i < 6; i++ ) {
			const row = this.calendarBodyNode.insertRow( i );
			this.lastRowNode = row;
			for ( let j = 0; j < 7; j++ ) {
				const cell = document.createElement( 'td' );

				cell.tabIndex = -1;
				cell.addEventListener( 'click', this.handleDayClick.bind( this ) );
				cell.addEventListener( 'keydown', this.handleDayKeyDown.bind( this ) );
				cell.addEventListener( 'focus', this.handleDayFocus.bind( this ) );

				cell.textContent = '-1';

				row.appendChild( cell );
				this.days.push( cell );
			}
		}

		this.updateGrid();
		this.close( false );
		this.setDateForButtonLabel();
	}

	updateGrid() {
		let flag;
		const focusedDay = this.focusedDay;

		this.titleNode.textContent = MONTH_LABELS[ focusedDay.getMonth() ] + ' ' + focusedDay.getFullYear();

		const firstDayOfMonth = new Date( focusedDay.getFullYear(), focusedDay.getMonth(), 1 );
		const dayOfWeek = firstDayOfMonth.getDay();

		firstDayOfMonth.setDate( firstDayOfMonth.getDate() - dayOfWeek );

		const firstDay = new Date( firstDayOfMonth );

		for ( let i = 0; i < this.days.length; i++ ) {
			flag = firstDay.getMonth() !== focusedDay.getMonth();
			this.updateDate( this.days[ i ], flag, firstDay, isSameDay( firstDay, this.selectedDay ) );
			firstDay.setDate( firstDay.getDate() + 1 );

			// Hide last row if all dates are disabled (e.g. in next month)
			if ( i === 35 ) {
				if ( flag ) {
					this.lastRowNode.style.visibility = 'hidden';
				} else {
					this.lastRowNode.style.visibility = 'visible';
				}
			}
		}

		this.noticeNode.textContent = '';
		this.fetchEvents();
	}

	fetchEvents() {
		if ( ! this.searchEndpoint ) {
			return;
		}

		const params = new URLSearchParams( {
			year: this.focusedDay.getFullYear(),
			month: this.focusedDay.getMonth() + 1,
		} );

		fetch( this.searchEndpoint + '?' + params.toString() )
			.then( response => {
				if ( ! response.ok ) {
					throw new Error( `Discover Request Status: ${ response.status }` );
				}

				return response.json();
			} )
			.then( results => {
				if ( results.length > 0 ) {
					this.days.forEach( day => {
						if ( ! isDisabled( day ) ) {
							const events = results.find( result => result.date === day.dataset.date )?.events || [];
							if ( events.length > 0 ) {
								day.dataset.events = events.join( ',' );
							}
						}
					} );

					this.noticeNode.textContent = 'Days with events are circled';
				}
			} )
			.catch( () => {
				// do nothing
			} );
	}

	updateDate( target, disable, day, selected ) {
		target.tabIndex = -1;
		target.removeAttribute( 'aria-selected' );
		target.dataset.date = formatDate( day );
		delete target.dataset.events;

		if ( disable ) {
			target.classList.add( 'disabled' );
			target.textContent = '';
		} else {
			target.classList.remove( 'disabled' );
			target.textContent = day.getDate();
			if ( selected ) {
				target.setAttribute( 'aria-selected', 'true' );
				target.tabIndex = 0;
			}
		}
	}

	moveFocusToDay( newDay ) {
		const prevDay = this.focusedDay;

		this.focusedDay = newDay;

		if ( ! isSameMonth( prevDay, newDay ) ) {
			this.updateGrid();
		}

		this.setFocusedDay();
	}

	setFocusedDay( flag ) {
		if ( typeof flag !== 'boolean' ) {
			flag = true;
		}

		for ( let i = 0; i < this.days.length; i++ ) {
			const dayNode = this.days[ i ];
			const day = getDateFromDateData( dayNode );

			dayNode.tabIndex = -1;
			if ( isSameDay( day, this.focusedDay ) ) {
				dayNode.tabIndex = 0;
				if ( flag ) {
					dayNode.focus();
				}
			}
		}
	}

	open() {
		this.dialogNode.style.display = 'block';
		this.dialogNode.style.zIndex = 2;

		this.getDateFromTextbox();
		this.updateGrid();
	}

	isOpen() {
		return window.getComputedStyle( this.dialogNode ).display !== 'none';
	}

	close( flag ) {
		if ( typeof flag !== 'boolean' ) {
			// Default is to move focus to combobox
			flag = true;
		}

		this.setMessage( '' );
		this.dialogNode.style.display = 'none';

		if ( flag ) {
			this.toggleButtonNode.focus();
		}
	}

	moveToNextMonth() {
		this.focusedDay.setMonth( this.focusedDay.getMonth() + 1 );
		this.updateGrid();
	}

	moveToPreviousMonth() {
		this.focusedDay.setMonth( this.focusedDay.getMonth() - 1 );
		this.updateGrid();
	}

	moveFocusToNextDay() {
		const newDate = new Date( this.focusedDay );
		newDate.setDate( newDate.getDate() + 1 );
		this.moveFocusToDay( newDate );
	}

	moveFocusToNextWeek() {
		const newDate = new Date( this.focusedDay );
		newDate.setDate( newDate.getDate() + 7 );
		this.moveFocusToDay( newDate );
	}

	moveFocusToPreviousDay() {
		const newDate = new Date( this.focusedDay );
		newDate.setDate( newDate.getDate() - 1 );
		this.moveFocusToDay( newDate );
	}

	moveFocusToPreviousWeek() {
		const newDate = new Date( this.focusedDay );
		newDate.setDate( newDate.getDate() - 7 );
		this.moveFocusToDay( newDate );
	}

	moveFocusToFirstDayOfWeek() {
		const newDate = new Date( this.focusedDay );
		newDate.setDate( newDate.getDate() - newDate.getDay() );
		this.moveFocusToDay( newDate );
	}

	moveFocusToLastDayOfWeek() {
		const newDate = new Date( this.focusedDay );
		newDate.setDate( newDate.getDate() + ( 6 - newDate.getDay() ) );
		this.moveFocusToDay( newDate );
	}

	updateInput( target ) {
		let date = this.focusedDay;

		if ( target ) {
			date = getDateFromDateData( target );
			// updated aria-selected
			this.days.forEach( day =>
				day === target
					? day.setAttribute( 'aria-selected', 'true' )
					: day.removeAttribute( 'aria-selected' ),
			);
		}

		this.inputNode.value = formatDate( date );
		this.inputNode.dispatchEvent( new Event( 'change' ) );

		this.setDateForButtonLabel();
	}

	getDateFromTextbox() {
		if ( this.inputNode.value ) {
			const [ year, month, day ] = this.inputNode.value.split( '-' ).map( v => parseInt( v ) );

			this.focusedDay = new Date( year, month - 1, day );
			this.selectedDay = new Date( this.focusedDay );
		} else {
			// Default to today's date
			this.focusedDay = new Date();
			this.selectedDay = new Date( 0, 0, 1 );
		}
	}

	setDateForButtonLabel() {
		if ( this.inputNode.value ) {
			const [ year, month, day ] = this.inputNode.value.split( '-' ).map( v => parseInt( v ) );
			const date = new Date( year, month - 1, day );

			let label = BUTTON_LABEL_CHANGE;
			label += ', ' + DAY_LABELS[ date.getDay() ];
			label += ' ' + MONTH_LABELS[ date.getMonth() ];
			label += ' ' + date.getDate();
			label += ', ' + date.getFullYear();

			this.toggleButtonNode.setAttribute( 'aria-label', label );
		} else {
			// If not a valid date, initialize with "Choose Date"
			this.toggleButtonNode.setAttribute( 'aria-label', BUTTON_LABEL_CHOOSE );
		}
	}

	setMessage( str ) {
		function setMessageDelayed() {
			this.messageNode.textContent = str;
		}

		if ( str !== this.lastMessage ) {
			setTimeout( setMessageDelayed.bind( this ), 200 );
			this.lastMessage = str;
		}
	}

	handleOkButton( event ) {
		let flag = false;

		switch ( event.type ) {
			case 'keydown':
				switch ( event.key ) {
					case 'Tab':
						if ( ! event.shiftKey ) {
							this.prevYearNode.focus();
							flag = true;
						}
						break;

					case 'Esc':
					case 'Escape':
						this.close();
						flag = true;
						break;

					default:
						break;
				}
				break;

			case 'click':
				this.updateInput();
				this.close();
				flag = true;
				break;

			default:
				break;
		}

		if ( flag ) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	handleCancelButton( event ) {
		let flag = false;

		switch ( event.type ) {
			case 'keydown':
				switch ( event.key ) {
					case 'Esc':
					case 'Escape':
						this.close();
						flag = true;
						break;

					default:
						break;
				}
				break;

			case 'click':
				this.close();
				flag = true;
				break;

			default:
				break;
		}

		if ( flag ) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	handleNextYearButton( event ) {
		let flag = false;

		switch ( event.type ) {
			case 'keydown':
				switch ( event.key ) {
					case 'Esc':
					case 'Escape':
						this.close();
						flag = true;
						break;

					case 'Enter':
						this.moveToNextYear();
						this.setFocusedDay( false );
						flag = true;
						break;
				}

				break;

			case 'click':
				this.moveToNextYear();
				this.setFocusedDay( false );
				break;

			default:
				break;
		}

		if ( flag ) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	handlePreviousYearButton( event ) {
		let flag = false;

		switch ( event.type ) {
			case 'keydown':
				switch ( event.key ) {
					case 'Enter':
						this.moveToPreviousYear();
						this.setFocusedDay( false );
						flag = true;
						break;

					case 'Tab':
						if ( event.shiftKey ) {
							this.oktoggleButtonNode.focus();
							flag = true;
						}
						break;

					case 'Esc':
					case 'Escape':
						this.close();
						flag = true;
						break;

					default:
						break;
				}

				break;

			case 'click':
				this.moveToPreviousYear();
				this.setFocusedDay( false );
				break;

			default:
				break;
		}

		if ( flag ) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	handleNextMonthButtonNode( event ) {
		let flag = false;

		switch ( event.type ) {
			case 'keydown':
				switch ( event.key ) {
					case 'Esc':
					case 'Escape':
						this.close();
						flag = true;
						break;

					case 'Enter':
						this.moveToNextMonth();
						this.setFocusedDay( false );
						flag = true;
						break;
				}

				break;

			case 'click':
				this.moveToNextMonth();
				this.setFocusedDay( false );
				break;

			default:
				break;
		}

		if ( flag ) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	handlePreviousMonthButton( event ) {
		let flag = false;

		switch ( event.type ) {
			case 'keydown':
				switch ( event.key ) {
					case 'Esc':
					case 'Escape':
						this.close();
						flag = true;
						break;

					case 'Enter':
						this.moveToPreviousMonth();
						this.setFocusedDay( false );
						flag = true;
						break;
				}

				break;

			case 'click':
				this.moveToPreviousMonth();
				this.setFocusedDay( false );
				flag = true;
				break;

			default:
				break;
		}

		if ( flag ) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	handleDayKeyDown( event ) {
		let flag = false;

		switch ( event.key ) {
			case 'Esc':
			case 'Escape':
				this.close();
				break;

			case ' ':
				this.updateInput( event.currentTarget );
				flag = true;
				break;

			case 'Enter':
				this.updateInput( event.currentTarget );
				this.close();
				flag = true;
				break;

			case 'Tab':
				this.canceltoggleButtonNode.focus();
				if ( event.shiftKey ) {
					this.nextYearNode.focus();
				}
				this.setMessage( '' );
				flag = true;
				break;

			case 'Right':
			case 'ArrowRight':
				this.moveFocusToNextDay();
				flag = true;
				break;

			case 'Left':
			case 'ArrowLeft':
				this.moveFocusToPreviousDay();
				flag = true;
				break;

			case 'Down':
			case 'ArrowDown':
				this.moveFocusToNextWeek();
				flag = true;
				break;

			case 'Up':
			case 'ArrowUp':
				this.moveFocusToPreviousWeek();
				flag = true;
				break;

			case 'PageUp':
				if ( event.shiftKey ) {
					this.moveToPreviousYear();
				} else {
					this.moveToPreviousMonth();
				}
				this.setFocusedDay();
				flag = true;
				break;

			case 'PageDown':
				if ( event.shiftKey ) {
					this.moveToNextYear();
				} else {
					this.moveToNextMonth();
				}
				this.setFocusedDay();
				flag = true;
				break;

			case 'Home':
				this.moveFocusToFirstDayOfWeek();
				flag = true;
				break;

			case 'End':
				this.moveFocusToLastDayOfWeek();
				flag = true;
				break;
		}

		if ( flag ) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	handleDayClick( event ) {
		if ( ! isDisabled( event.currentTarget ) && event.which !== 3 ) {
			this.updateInput( event.currentTarget );
			this.close();
		}

		event.stopPropagation();
		event.preventDefault();
	}

	handleDayFocus() {
		this.setMessage( this.messageCursorKeys );
	}

	handleButtonKeydown( event ) {
		if ( event.key === 'Enter' || event.key === ' ' ) {
			this.open();
			this.setFocusedDay();

			event.stopPropagation();
			event.preventDefault();
		}
	}

	handleButtonClick( event ) {
		if ( this.isOpen() ) {
			this.close();
		} else {
			this.open();
			this.setFocusedDay();
		}

		event.stopPropagation();
		event.preventDefault();
	}

	handleBackgroundMouseUp( event ) {
		if (
			! this.toggleButtonNode.contains( event.target ) &&
			! this.dialogNode.contains( event.target )
		) {
			if ( this.isOpen() ) {
				this.close( false );
				event.stopPropagation();
				event.preventDefault();
			}
		}
	}
}

export default function setupDatePickers( searchEndpoint ) {
	document.querySelectorAll( '.js-datepicker' ).forEach( widget => {
		new DatePickerDialog( widget, searchEndpoint );
	} );
}
